/**
 * Модальный лист снизу — общий для приложения и онбординга.
 *
 * Вынесен в отдельный файл, потому что второй такой же лист неизбежно разошёлся
 * бы с первым: разные отступы, разное поведение подложки, разная кнопка
 * закрытия. Пользователю это читается как два разных приложения.
 */
import { CloseX } from './close-icon.js';
import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icon.js';
import { Tip } from './tip.js';
import { useSwipeClose } from './sheet-swipe.js';

/*
 * Пока открыт лист, страница под ним стоит на месте.
 *
 * Иначе движение пальца по подложке прокручивает главную: человек закрывает
 * лист и обнаруживает себя в другом месте экрана, хотя ничего там не делал.
 *
 * Страницу не фиксируем. Прежде `body` на время листа получал
 * `position: fixed`, и в установленном на экран приложении iPhone (строка
 * состояния поверх содержимого) видимая область от этого укорачивалась на
 * высоту строки состояния: лист упирался в укороченный низ, а под ним
 * оставалась полоса в 59px. Теперь прокрутку снимает `overflow: hidden` у
 * страницы, а касания вне тела листа гасятся: мобильный Safari одного
 * `overflow` не всегда слушается. Прокрутка внутри листа не цепляет страницу
 * — у тела листа `overscroll-behavior: contain`.
 *
 * Счётчик нужен из-за вложенных листов: выбор счёта открывается поверх формы
 * операции, и его закрытие не должно отпускать страницу, пока форма открыта.
 */
let openSheets = 0;

/** Касание вне тела листа страницу не двигает. */
const stillPage = (e: TouchEvent) => {
  const target = e.target instanceof Element ? e.target : null;
  if (target?.closest('.sheet .body')) return;
  e.preventDefault();
};

const holdPage = () => {
  if (openSheets++ > 0) return;
  document.documentElement.style.overflow = 'hidden';
  document.body.style.overflow = 'hidden';
  document.addEventListener('touchmove', stillPage, { passive: false });
};

const releasePage = () => {
  openSheets = Math.max(0, openSheets - 1);
  if (openSheets > 0) return;
  document.documentElement.style.overflow = '';
  document.body.style.overflow = '';
  document.removeEventListener('touchmove', stillPage);
};

/*
 * Открытый лист — единственное, с чем можно работать.
 *
 * Страница и нижние листы получают `inert`: Tab и экранный диктор больше не
 * уходят на подложку. Раньше Shift+Tab из «Условий» попадал на кнопки
 * главной под размытием. Стопка нужна из-за вложенных листов: закрылся
 * верхний — оживает тот, что под ним, а не страница.
 */
const stack: HTMLElement[] = [];

const setInert = (el: Element | null, on: boolean) => {
  if (!el) return;
  if (on) el.setAttribute('inert', '');
  else el.removeAttribute('inert');
};

const pushSheet = (el: HTMLElement) => {
  stack.forEach((s) => setInert(s, true));
  stack.push(el);
  setInert(document.getElementById('root'), true);
};

const popSheet = (el: HTMLElement) => {
  const at = stack.indexOf(el);
  if (at >= 0) stack.splice(at, 1);
  const top = stack[stack.length - 1];
  if (top) setInert(top, false);
  else setInert(document.getElementById('root'), false);
};

/** «Уменьшить движение» в системе — листы появляются и уходят без анимации. */
const calm = (): boolean => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Поле ввода: вернуть в него фокус значит открыть клавиатуру без спроса. */
const typing = (el: Element | null) =>
  !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT'
    || (el as HTMLElement).isContentEditable);

export const SheetWrap = (p: {
  title: string; onClose: () => void; children: ReactNode;
  /** Без строки заголовка: у листа операции её место занимают вкладки. */
  bare?: boolean;
  operationHeight?: number;
  keyboardInset?: number;
  /** Лист операции, пишут заметку: лист по содержимому стоит на системной клавиатуре. */
  typing?: boolean;
  /**
   * Второй шаг внутри листа: в заголовке появляется «назад», а крестик
   * уходит. Закрыть лист крестиком с середины пути значит потерять
   * заполненное, и предлагать это рядом с «назад» не стоит.
   */
  onBack?: () => void;
  /** Куда ведёт «назад» — для диктора: «Назад к категории», «Назад к разговору». */
  backLabel?: string;
  /** Главное действие шага — в заголовке, справа. */
  action?: ReactNode;
  /** Свой облик листа по макету: класс на `.sheet`. */
  className?: string;
  /** Пояснение ко всему листу: значок «i» у заголовка. */
  tip?: ReactNode;
}) => {
  useEffect(() => { holdPage(); return releasePage; }, []);
  /*
   * Лист уезжает вниз и при закрытии (16.10.2026), а не исчезает.
   *
   * Закрывают его по-разному: крестиком, «Сохранить», переходом в другой
   * лист, — и React убирает разметку сразу. Поэтому на её месте на четверть
   * секунды остаётся копия: она уезжает вниз, затемнение гаснет, и копия
   * удаляется. Нажать в ней ничего нельзя, диктор её не читает.
   */
  /** Лист уже уехал вниз под пальцем — копия для ухода не нужна. */
  const swiped = useRef(false);
  useLayoutEffect(() => {
    const el = root.current;
    return () => {
      if (!el || typeof document === 'undefined' || calm() || swiped.current) return;
      const ghost = el.cloneNode(true) as HTMLElement;
      ghost.classList.add('closing');
      ghost.setAttribute('aria-hidden', 'true');
      ghost.inert = true;
      document.body.appendChild(ghost);
      const gone = () => ghost.remove();
      ghost.addEventListener('animationend', (e) => { if ((e.target as Element).classList.contains('body')) gone(); });
      window.setTimeout(gone, 450);
    };
  }, []);
  /*
   * Клавиатура: лист заканчивается над ней, а не под ней.
   *
   * Без этого системная клавиатура накрывала низ листа, и Safari, пытаясь
   * показать поле, сдвигал видимую область — подложка съезжала вместе с ней,
   * и сверху вылезал белый край листа, лежащего ниже. Высоту берём из
   * видимой области; разница меньше 120 px клавиатурой не считается —
   * столько отнимают панели браузера.
   */
  const [keyboard, setKeyboard] = useState(0);
  useEffect(() => {
    const v = typeof window === 'undefined' ? null : window.visualViewport;
    if (!v) return;
    const update = () => {
      // Щипок пальцами — действие человека, а не клавиатура.
      if (Math.abs(v.scale - 1) > 0.05) return;
      const inset = Math.max(0, window.innerHeight - v.height - v.offsetTop);
      setKeyboard(inset > 120 ? Math.round(inset) : 0);
    };
    update();
    v.addEventListener('resize', update);
    v.addEventListener('scroll', update);
    return () => { v.removeEventListener('resize', update); v.removeEventListener('scroll', update); };
  }, []);
  /*
   * Открытый лист не съёживается под содержимое.
   *
   * В справочнике категорий переключение «Расходы / Доходы» меняло число
   * строк — и лист прыгал под пальцем: кнопка, по которой человек только что
   * попал, оказывалась в другом месте экрана. Поэтому запомненная высота
   * только растёт: содержимое короче прежнего прокручивается внутри, длиннее
   * — раздвигает лист до потолка в 92 % экрана и дальше не идёт.
   *
   * Лист операции сюда не попадает: у него своя фиксированная высота,
   * измеренная при открытии, чтобы клавиатура не двигала форму.
   */
  const body = useRef<HTMLDivElement>(null);
  const peak = useRef(0);
  useEffect(() => {
    const el = body.current;
    if (!el || p.operationHeight) return;
    const fit = () => {
      // Потолок вычисляется заново: с открытой клавиатурой он ниже, и
      // запомненная высота не должна держать лист под ней.
      const cap = parseFloat(getComputedStyle(el).maxHeight);
      const top = Number.isFinite(cap) ? cap : Infinity;
      peak.current = Math.max(peak.current, Math.min(el.scrollHeight, top));
      const want = Math.min(peak.current, top);
      const now = `${Math.round(want)}px`;
      if (el.style.minHeight !== now) el.style.minHeight = now;
    };
    fit();
    // В тестовой среде наблюдателя может не быть: тогда высота просто не
    // запоминается — это оформление, а не поведение денег.
    if (typeof ResizeObserver === 'undefined') return;
    const watch = new ResizeObserver(fit);
    watch.observe(el);
    return () => watch.disconnect();
  }, [p.operationHeight, keyboard]);
  /*
   * Фокус входит в лист и возвращается туда, откуда его открыли.
   *
   * Кто открыл лист, запоминаем при первой отрисовке: к эффекту поле внутри
   * могло уже взять фокус само (`autoFocus`) — тогда его не трогаем.
   * Escape закрывает только верхний лист, а на вложенном шаге ведёт назад.
   */
  const root = useRef<HTMLDivElement>(null);
  const opener = useRef(typeof document === 'undefined' ? null : document.activeElement);
  const latest = useRef(p);
  latest.current = p;
  useEffect(() => {
    const el = root.current;
    const box = body.current;
    if (!el || !box) return;
    const prev = opener.current;
    pushSheet(el);
    if (!box.contains(document.activeElement)) box.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || stack[stack.length - 1] !== el) return;
      e.preventDefault();
      const { onBack, onClose } = latest.current;
      (onBack ?? onClose)();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      popSheet(el);
      if (prev instanceof HTMLElement && prev.isConnected && !typing(prev)) prev.focus({ preventScroll: true });
    };
  }, []);
  /*
   * Свайп вниз закрывает лист, как крестик (`sheet-swipe.ts`). На втором
   * шаге — нет: там нет и крестика, чтобы не потерять заполненное.
   */
  useSwipeClose(root, body, !p.onBack, () => latest.current.onClose(), (gone) => { swiped.current = gone; });
  const titleId = useId();
  /*
   * Лист рисуется прямо в `body`, а не там, где его позвали.
   *
   * Второй лист открывается изнутри первого — выбор категории из формы
   * операции, — и в дереве оказывался внутри его прокручиваемого тела. Тело
   * анимируется сдвигом, а элемент с анимацией сдвига становится системой
   * отсчёта для всего, что внутри «прибито к экрану»: подложка второго листа
   * переставала считаться от экрана и обрезалась телом первого. Сверху
   * оставалась белая полоса — край листа, лежащего ниже. Перенос в `body`
   * убирает и обрезку, и спор за порядок отрисовки; React-события ходят по
   * своему дереву и работают как прежде.
   */
  const sheet = (
    <div ref={root} className={`sheet${p.operationHeight ? ' operation-sheet' : ''}${p.operationHeight && p.typing ? ' typing' : ''}${p.className ? ` ${p.className}` : ''}`} style={{ ...(p.operationHeight
      ? { '--operation-height': `${p.operationHeight}px`, '--keyboard-inset': `${p.keyboardInset ?? 0}px` }
      : { '--kb': `${keyboard}px` }) } as CSSProperties}>
      <div className="bg" onClick={p.onClose} />
      <div className={`body${p.bare ? ' bare' : ''}`} ref={body} role="dialog" aria-modal="true" tabIndex={-1}
        {...(p.bare ? { 'aria-label': p.title } : { 'aria-labelledby': titleId })}>
        <div className="grab" />
        {!p.bare && (
          <div className={`sheethead${p.onBack ? ' step' : ''}`}>
            {p.onBack && <button className="icobtn" onClick={p.onBack} aria-label={p.backLabel ?? 'Назад'}><Icon name="chevron-left" size={16} /></button>}
            {p.tip
              ? <span className="sheettitle"><b id={titleId}>{p.title}</b><Tip>{p.tip}</Tip></span>
              : <b id={titleId}>{p.title}</b>}
            {p.action}
            {!p.onBack && <button className="icobtn" onClick={p.onClose} aria-label="Закрыть"><CloseX /></button>}
          </div>
        )}
        {p.operationHeight ? <div className="operation-scroll">{p.children}</div> : p.children}
      </div>
    </div>
  );
  return typeof document === 'undefined' ? sheet : createPortal(sheet, document.body);
};

/** Карандаш в строке списка: показывает, что запись правится. */
export const PencilIcon = () => <Icon name="pen" size={15} />;
